"use client";

import { useCallback, useEffect, useState } from "react";
import { z } from "zod";
import {
  BookOpen,
  ChevronDown,
  ChevronRight,
  LineChart,
  RotateCcw,
  Table2,
  TrendingUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { CalculatorTemplate } from "@/components/calculator/CalculatorTemplate";
import { InputForm } from "@/components/calculator/InputForm";
import { ResultCard } from "@/components/calculator/ResultCard";
import { LineChartWrapper, PieChartWrapper } from "@/components/calculator/ChartWrapperLazy";
import { ExplanationTable } from "@/components/calculator/ExplanationTable";
import { ShareButtons } from "@/components/calculator/ShareButtons";
import { Input } from "@/components/ui/input";
import { formatCurrency, type Currency } from "@/lib/utils/currency";
import {
  calculateInvestment,
  calculateInvestmentScenarios,
  type ContributionFrequency,
  type CompoundingFrequency,
  type SolveMode,
} from "@/lib/calculators/investment";

const MAX_AMOUNT = 999_999_999_999;

const contributionFreqOptions: { value: ContributionFrequency; label: string }[] = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "annually", label: "Annually" },
];

const compoundingFreqOptions: { value: CompoundingFrequency; label: string }[] = [
  { value: "daily", label: "Daily" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "annually", label: "Annually" },
  { value: "continuously", label: "Continuously" },
];

const schema = z.object({
  initialInvestment: z.coerce
    .number()
    .min(0, "Must be 0 or more")
    .max(MAX_AMOUNT, "Maximum is $999 trillion"),
  monthlyContribution: z.coerce
    .number()
    .min(0, "Must be 0 or more")
    .max(MAX_AMOUNT),
  contributionFrequency: z.enum(["monthly", "quarterly", "annually"]),
  expectedReturn: z.coerce.number().min(0, "Enter a valid rate").max(15),
  years: z.coerce.number().min(0.1, "Enter at least 0.1 year").max(50),
  compoundingFrequency: z.enum([
    "daily",
    "monthly",
    "quarterly",
    "annually",
    "continuously",
  ]),
  adjustForInflation: z.boolean(),
  inflationRate: z.coerce.number().min(0).max(10),
  taxRate: z.coerce.number().min(0).max(50),
  solveMode: z.enum(["futureValue", "monthlyContribution", "years"]),
  targetFutureValue: z.coerce.number().min(0, "Must be 0 or more").max(MAX_AMOUNT),
  currency: z.enum(["USD", "EUR", "GBP", "CAD"]),
});

type FormValues = z.infer<typeof schema>;

const defaultValues: FormValues = {
  initialInvestment: 10000,
  monthlyContribution: 500,
  contributionFrequency: "monthly",
  expectedReturn: 7,
  years: 20,
  compoundingFrequency: "monthly",
  adjustForInflation: false,
  inflationRate: 2.5,
  taxRate: 0,
  solveMode: "futureValue",
  targetFutureValue: 500000,
  currency: "USD",
};

const CURRENCY_OPTIONS: { value: Currency; label: string }[] = [
  { value: "USD", label: "USD ($)" },
  { value: "EUR", label: "EUR (€)" },
  { value: "GBP", label: "GBP (£)" },
  { value: "CAD", label: "CAD ($)" },
];

const PRESETS: { label: string; values: Partial<FormValues> }[] = [
  {
    label: "Stocks (7%)",
    values: {
      initialInvestment: 10000,
      monthlyContribution: 500,
      expectedReturn: 7,
      years: 20,
    },
  },
  {
    label: "Bonds (4%)",
    values: {
      initialInvestment: 50000,
      monthlyContribution: 200,
      expectedReturn: 4,
      years: 10,
    },
  },
  {
    label: "Mixed (6%)",
    values: {
      initialInvestment: 25000,
      monthlyContribution: 400,
      expectedReturn: 6,
      years: 25,
    },
  },
  {
    label: "Savings (4.5%)",
    values: {
      initialInvestment: 5000,
      monthlyContribution: 300,
      expectedReturn: 4.5,
      years: 5,
    },
  },
];

const CHART_COLOR = "#2563eb";
const SCENARIO_COLORS = {
  low: "#2563eb",
  medium: "#dc2626",
  high: "#16a34a",
} as const;

function useMounted() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return mounted;
}

function parseValuesFromSearchParams(
  params: URLSearchParams
): Partial<FormValues> | null {
  const initial = params.get("initial");
  const rate = params.get("rate");
  if (!initial || !rate) return null;
  const parsed: Partial<FormValues> = {
    initialInvestment: parseFloat(initial),
    monthlyContribution: params.get("contrib")
      ? parseFloat(params.get("contrib")!)
      : 0,
    expectedReturn: parseFloat(rate),
    years: params.get("years") ? parseInt(params.get("years")!, 10) : 20,
    adjustForInflation: params.get("inflation") === "1",
    inflationRate: params.get("inflationRate")
      ? parseFloat(params.get("inflationRate")!)
      : 2.5,
    solveMode:
      (params.get("solve") as FormValues["solveMode"]) ?? "futureValue",
    targetFutureValue: params.get("target")
      ? parseFloat(params.get("target")!)
      : 500000,
    taxRate: params.get("tax") ? parseFloat(params.get("tax")!) : 0,
    currency: (params.get("currency") as Currency) ?? "USD",
  };
  return parsed;
}

function buildShareUrl(values: FormValues): string {
  const params = new URLSearchParams();
  params.set("initial", String(values.initialInvestment));
  params.set("contrib", String(values.monthlyContribution));
  params.set("rate", String(values.expectedReturn));
  params.set("years", String(values.years));
  params.set("solve", values.solveMode);
  params.set("target", String(values.targetFutureValue));
  params.set("currency", values.currency);
  if (values.taxRate > 0) params.set("tax", String(values.taxRate));
  if (values.adjustForInflation) {
    params.set("inflation", "1");
    params.set("inflationRate", String(values.inflationRate));
  }
  if (typeof window !== "undefined") {
    return `${window.location.origin}${window.location.pathname}?${params.toString()}`;
  }
  return "";
}

/** Merge scenario chart data for multi-line chart */
function mergeScenarioChartData(
  scenarios: ReturnType<typeof calculateInvestmentScenarios>
): { label: string; low: number; medium: number; high: number }[] {
  const maxYear = Math.max(
    scenarios.low.chartData[scenarios.low.chartData.length - 1]?.year ?? 0,
    scenarios.medium.chartData[scenarios.medium.chartData.length - 1]?.year ?? 0,
    scenarios.high.chartData[scenarios.high.chartData.length - 1]?.year ?? 0
  );
  const lowByYear = new Map(
    scenarios.low.chartData.map((d) => [d.year, d.balance])
  );
  const medByYear = new Map(
    scenarios.medium.chartData.map((d) => [d.year, d.balance])
  );
  const highByYear = new Map(
    scenarios.high.chartData.map((d) => [d.year, d.balance])
  );
  const merged: { label: string; low: number; medium: number; high: number }[] =
    [];
  for (let y = 0; y <= maxYear; y++) {
    merged.push({
      label: `Year ${y}`,
      low: lowByYear.get(y) ?? 0,
      medium: medByYear.get(y) ?? 0,
      high: highByYear.get(y) ?? 0,
    });
  }
  return merged;
}

export function InvestmentCalculator() {
  const mounted = useMounted();
  const [formValues, setFormValues] = useState<FormValues>(defaultValues);
  const [resetKey, setResetKey] = useState(0);
  const [result, setResult] = useState(() =>
    calculateInvestment({
      ...defaultValues,
    })
  );
  const [scenarios, setScenarios] = useState(() =>
    calculateInvestmentScenarios({
      ...defaultValues,
    })
  );
  const [showYearlyTable, setShowYearlyTable] = useState(false);
  const [yearlyTableRowsShown, setYearlyTableRowsShown] = useState(12);

  const recalc = useCallback((values: FormValues) => {
    const input = {
      initialInvestment: values.initialInvestment,
      monthlyContribution: values.monthlyContribution,
      contributionFrequency: values.contributionFrequency,
      expectedReturn: values.expectedReturn,
      years: values.years,
      compoundingFrequency: values.compoundingFrequency,
      adjustForInflation: values.adjustForInflation,
      inflationRate: values.inflationRate,
      taxRate: values.taxRate,
      solveMode: values.solveMode,
      targetFutureValue: values.targetFutureValue,
    };
    setResult(calculateInvestment(input));
    setScenarios(calculateInvestmentScenarios(input));
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const parsed = parseValuesFromSearchParams(params);
    if (parsed) {
      const merged = { ...defaultValues, ...parsed } as FormValues;
      const parsedResult = schema.safeParse(merged);
      if (parsedResult.success) {
        setFormValues(parsedResult.data);
        recalc(parsedResult.data);
        setResetKey((k) => k + 1);
      }
    }
  }, [recalc]);

  const handleReset = useCallback(() => {
    setFormValues(defaultValues);
    recalc(defaultValues);
    setResetKey((k) => k + 1);
  }, [recalc]);

  const handlePreset = useCallback(
    (preset: Partial<FormValues>) => {
      const merged = { ...defaultValues, ...formValues, ...preset } as FormValues;
      const parsed = schema.safeParse(merged);
      if (parsed.success) {
        setFormValues(parsed.data);
        recalc(parsed.data);
        setResetKey((k) => k + 1);
      }
    },
    [formValues, recalc]
  );

  const handleChange = useCallback(
    (values: FormValues) => {
      setFormValues((prev) => {
        const next = {
          ...values,
          adjustForInflation: prev.adjustForInflation,
          inflationRate: prev.inflationRate,
          taxRate: values.taxRate ?? prev.taxRate,
          solveMode: values.solveMode ?? prev.solveMode,
          targetFutureValue: values.targetFutureValue ?? prev.targetFutureValue,
          currency: values.currency ?? prev.currency,
        };
        recalc(next);
        return next;
      });
    },
    [recalc]
  );

  const handleSolveModeChange = useCallback(
    (solveMode: SolveMode) => {
      const next = { ...formValues, solveMode };
      setFormValues(next);
      recalc(next);
    },
    [formValues, recalc]
  );

  const handleCurrencyChange = useCallback(
    (currency: Currency) => {
      const next = { ...formValues, currency };
      setFormValues(next);
      recalc(next);
    },
    [formValues, recalc]
  );

  const fmt = useCallback(
    (value: number) => formatCurrency(value, formValues.currency),
    [formValues.currency]
  );

  const handleInflationChange = useCallback(
    (updates: { adjustForInflation?: boolean; inflationRate?: number }) => {
      const next = { ...formValues, ...updates };
      setFormValues(next);
      recalc(next);
    },
    [formValues, recalc]
  );

  const scenarioChartData = mergeScenarioChartData(scenarios);
  const singleChartData = result.chartData.map((d) => ({
    label: `Year ${d.year}`,
    balance: d.balance,
  }));

  const pieData = [
    {
      name: "Principal + contributions",
      value: result.principalVsInterest.principal,
      color: "#64748b",
    },
    {
      name: "Interest earned",
      value: result.principalVsInterest.interest,
      color: "#2563eb",
    },
  ].filter((d) => d.value > 0);

  return (
    <CalculatorTemplate
      chartSectionLabel="Growth over time"
      chartSectionIcon={LineChart}
      explanationSectionLabel="Formula & examples"
      explanationSectionIcon={BookOpen}
      inputsSection={
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Currency
            </label>
            <div className="flex flex-wrap gap-2">
              {CURRENCY_OPTIONS.map((opt) => (
                <Button
                  key={opt.value}
                  type="button"
                  variant={
                    formValues.currency === opt.value ? "default" : "outline"
                  }
                  size="sm"
                  onClick={() => handleCurrencyChange(opt.value)}
                  className="text-xs"
                >
                  {opt.label}
                </Button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Solve for
            </label>
            <div className="flex flex-wrap gap-2">
              {(
                [
                  {
                    value: "futureValue" as SolveMode,
                    label: "Future value",
                    tooltip: "Project your balance at end of period.",
                  },
                  {
                    value: "monthlyContribution" as SolveMode,
                    label: "Monthly contribution",
                    tooltip: "How much to save each month to reach your goal.",
                  },
                  {
                    value: "years" as SolveMode,
                    label: "Years needed",
                    tooltip: "How long until you reach your goal.",
                  },
                ] as const
              ).map((opt) => (
                <Button
                  key={opt.value}
                  type="button"
                  variant={
                    formValues.solveMode === opt.value ? "default" : "outline"
                  }
                  size="sm"
                  onClick={() => handleSolveModeChange(opt.value)}
                  title={opt.tooltip}
                  className="text-xs"
                >
                  {opt.label}
                </Button>
              ))}
            </div>
          </div>

          <InputForm
            key={resetKey}
            schema={schema}
            defaultValues={formValues}
            fields={[
              {
                name: "initialInvestment",
                label: "Initial investment",
                tooltip: "Lump sum you're starting with (can be $0).",
                suffix: " USD",
                min: 0,
                max: MAX_AMOUNT,
                step: 1,
                inputMode: "decimal",
              },
              ...(formValues.solveMode === "futureValue" || formValues.solveMode === "years"
                ? [
                    {
                      name: "monthlyContribution" as const,
                      label: "Monthly contribution",
                      tooltip: "Amount you add each month (or scaled by frequency).",
                      suffix: " USD",
                      min: 0,
                      max: MAX_AMOUNT,
                      step: 1,
                      inputMode: "decimal" as const,
                    },
                  ]
                : []),
              ...(formValues.solveMode === "monthlyContribution" || formValues.solveMode === "years"
                ? [
                    {
                      name: "targetFutureValue" as const,
                      label: "Target amount",
                      tooltip: "The amount you want to reach.",
                      suffix: " USD",
                      min: 1,
                      max: MAX_AMOUNT,
                      step: 1,
                      inputMode: "decimal" as const,
                    },
                  ]
                : []),
              {
                name: "contributionFrequency",
                label: "Contribution frequency",
                tooltip: "How often you make contributions.",
                inputType: "select" as const,
                options: contributionFreqOptions.map((o) => ({
                  value: o.value,
                  label: o.label,
                })),
              },
              {
                name: "expectedReturn",
                label: "Expected annual return",
                tooltip: "Historical stock market average is ~7%. Bonds ~4%.",
                suffix: "%",
                min: 0,
                max: 15,
                step: 0.5,
                inputType: "slider-number" as const,
                inputMode: "decimal" as const,
              },
              ...(formValues.solveMode === "futureValue" || formValues.solveMode === "monthlyContribution"
                ? [
                    {
                      name: "years" as const,
                      label: "Time horizon",
                      tooltip: "How long you'll invest.",
                      suffix: " years",
                      min: 0.1,
                      max: 50,
                      sliderMax: 40,
                      step: 0.5,
                      inputType: "slider-number" as const,
                    },
                  ]
                : []),
              {
                name: "compoundingFrequency",
                label: "Compounding frequency",
                tooltip: "How often returns are reinvested.",
                inputType: "select" as const,
                options: compoundingFreqOptions.map((o) => ({
                  value: o.value,
                  label: o.label,
                })),
              },
            ]}
            onChange={handleChange}
          />

          <div className="space-y-2 rounded-lg border border-border bg-muted/30 px-4 py-3">
            <div className="flex items-center justify-between">
              <label
                htmlFor="adjust-inflation"
                className="text-sm font-medium text-foreground"
              >
                Adjust for inflation
              </label>
              <input
                id="adjust-inflation"
                type="checkbox"
                checked={formValues.adjustForInflation}
                onChange={(e) =>
                  handleInflationChange({ adjustForInflation: e.target.checked })
                }
                className="h-4 w-4 rounded border-input focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </div>
            {formValues.adjustForInflation && (
              <div className="flex items-center gap-2">
                <label htmlFor="inflation-rate" className="sr-only">
                  Inflation rate (percent)
                </label>
                <Input
                  id="inflation-rate"
                  type="number"
                  value={formValues.inflationRate}
                  onChange={(e) =>
                    handleInflationChange({
                      inflationRate: parseFloat(e.target.value) || 0,
                    })
                  }
                  min={0}
                  max={10}
                  step={0.5}
                  className="h-7 w-20"
                />
                <span className="text-sm text-muted-foreground">
                  % inflation rate
                </span>
              </div>
            )}
          </div>

          <div className="space-y-2 rounded-lg border border-border bg-muted/30 px-4 py-3">
            <label
              htmlFor="tax-rate"
              className="text-sm font-medium text-foreground"
            >
              Tax rate on gains
            </label>
            <div className="flex items-center gap-2">
              <Input
                id="tax-rate"
                type="number"
                value={formValues.taxRate}
                onChange={(e) => {
                  const next = {
                    ...formValues,
                    taxRate: Math.min(50, Math.max(0, parseFloat(e.target.value) || 0)),
                  };
                  setFormValues(next);
                  recalc(next);
                }}
                min={0}
                max={50}
                step={1}
                className="h-7 w-20"
              />
              <span className="text-sm text-muted-foreground">%</span>
            </div>
            <p className="text-xs text-muted-foreground">
              For taxable accounts. Tax applied to investment gains only.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground">Presets:</span>
            {PRESETS.map((p) => {
              const isSelected = Object.entries(p.values).every(
                ([k, v]) => formValues[k as keyof FormValues] === v
              );
              return (
                <Button
                  key={p.label}
                  type="button"
                  variant={isSelected ? "default" : "outline"}
                  size="sm"
                  onClick={() => handlePreset(p.values)}
                  className="text-xs"
                >
                  {p.label}
                </Button>
              );
            })}
          </div>

          <div className="flex gap-2">
            <ShareButtons shareUrl={buildShareUrl(formValues)} />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleReset}
              className="text-muted-foreground"
            >
              <RotateCcw className="size-3.5" />
              Reset
            </Button>
          </div>
        </div>
      }
      resultsSection={
        <div className="space-y-4">
          {formValues.solveMode === "monthlyContribution" &&
          result.solvedMonthlyContribution != null ? (
            <ResultCard
              icon={TrendingUp}
              metrics={[
                {
                  label: "Required monthly contribution",
                  value: fmt(result.solvedMonthlyContribution ?? 0),
                  highlight: true,
                  tooltip: "Amount to save each month to reach your goal.",
                },
                {
                  label: "Target amount",
                  value: fmt(formValues.targetFutureValue),
                  tooltip: "Your goal.",
                },
                {
                  label: "Total contributions",
                  value: fmt(result.totalContributions),
                  tooltip: "Sum of all contributions over the period.",
                },
                {
                  label: "Interest earned",
                  value: fmt(result.totalInterest),
                  tooltip: "Growth from investment returns.",
                },
                ...(result.afterTaxFutureValue != null
                  ? [
                      {
                        label: "After-tax value",
                        value: fmt(result.afterTaxFutureValue),
                        tooltip: "After taxes on investment gains.",
                      },
                    ]
                  : []),
              ]}
            />
          ) : formValues.solveMode === "years" &&
            result.solvedYears != null ? (
            <ResultCard
              icon={TrendingUp}
              metrics={[
                {
                  label: "Years to goal",
                  value:
                    result.solvedYears < 1
                      ? `${Math.round(result.solvedYears * 12)} months`
                      : `${result.solvedYears.toFixed(1)} years`,
                  highlight: true,
                  tooltip: "How long until you reach your goal.",
                },
                {
                  label: "Target amount",
                  value: fmt(formValues.targetFutureValue),
                  tooltip: "Your goal.",
                },
                {
                  label: "Total contributions",
                  value: fmt(result.totalContributions),
                  tooltip: "Sum of all contributions over the period.",
                },
                {
                  label: "Interest earned",
                  value: fmt(result.totalInterest),
                  tooltip: "Growth from investment returns.",
                },
                ...(result.afterTaxFutureValue != null
                  ? [
                      {
                        label: "After-tax value",
                        value: fmt(result.afterTaxFutureValue),
                        tooltip: "After taxes on investment gains.",
                      },
                    ]
                  : []),
              ]}
            />
          ) : (
            <ResultCard
              icon={TrendingUp}
              metrics={[
                {
                  label: "Future value",
                  value: fmt(result.futureValue),
                  highlight: true,
                  tooltip: "Projected balance at end of period.",
                },
                {
                  label: "Total contributions",
                  value: fmt(result.totalContributions),
                  tooltip: "Sum of all contributions made.",
                },
                {
                  label: "Interest earned",
                  value: fmt(result.totalInterest),
                  tooltip: "Growth from investment returns.",
                },
                ...(result.realFutureValue != null
                  ? [
                      {
                        label: "Real purchasing power",
                        value: fmt(result.realFutureValue ?? 0),
                        tooltip:
                          "Value in today's dollars (adjusted for inflation).",
                      },
                    ]
                  : []),
                ...(result.afterTaxFutureValue != null
                  ? [
                      {
                        label: "After-tax value",
                        value: fmt(result.afterTaxFutureValue),
                        tooltip: "After taxes on investment gains.",
                      },
                    ]
                  : []),
              ]}
            />
          )}

          <div className="grid gap-3 sm:grid-cols-3">
            {formValues.solveMode === "monthlyContribution" ? (
              <>
                <div className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-center">
                  <p className="text-xs font-medium text-muted-foreground">
                    Low (4%)
                  </p>
                  <p className="text-lg font-semibold text-foreground">
                    {fmt(scenarios.low.solvedMonthlyContribution ?? 0)}
                    <span className="ml-1 text-xs font-normal text-muted-foreground">
                      /mo
                    </span>
                  </p>
                </div>
                <div className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-center">
                  <p className="text-xs font-medium text-muted-foreground">
                    Medium (7%)
                  </p>
                  <p className="text-lg font-semibold text-foreground">
                    {fmt(scenarios.medium.solvedMonthlyContribution ?? 0)}
                    <span className="ml-1 text-xs font-normal text-muted-foreground">
                      /mo
                    </span>
                  </p>
                </div>
                <div className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-center">
                  <p className="text-xs font-medium text-muted-foreground">
                    High (10%)
                  </p>
                  <p className="text-lg font-semibold text-foreground">
                    {fmt(scenarios.high.solvedMonthlyContribution ?? 0)}
                    <span className="ml-1 text-xs font-normal text-muted-foreground">
                      /mo
                    </span>
                  </p>
                </div>
              </>
            ) : formValues.solveMode === "years" ? (
              <>
                <div className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-center">
                  <p className="text-xs font-medium text-muted-foreground">
                    Low (4%)
                  </p>
                  <p className="text-lg font-semibold text-foreground">
                    {scenarios.low.solvedYears != null
                      ? scenarios.low.solvedYears < 1
                        ? `${Math.round(scenarios.low.solvedYears * 12)} mo`
                        : `${scenarios.low.solvedYears.toFixed(1)} yr`
                      : "—"}
                  </p>
                </div>
                <div className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-center">
                  <p className="text-xs font-medium text-muted-foreground">
                    Medium (7%)
                  </p>
                  <p className="text-lg font-semibold text-foreground">
                    {scenarios.medium.solvedYears != null
                      ? scenarios.medium.solvedYears < 1
                        ? `${Math.round(scenarios.medium.solvedYears * 12)} mo`
                        : `${scenarios.medium.solvedYears.toFixed(1)} yr`
                      : "—"}
                  </p>
                </div>
                <div className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-center">
                  <p className="text-xs font-medium text-muted-foreground">
                    High (10%)
                  </p>
                  <p className="text-lg font-semibold text-foreground">
                    {scenarios.high.solvedYears != null
                      ? scenarios.high.solvedYears < 1
                        ? `${Math.round(scenarios.high.solvedYears * 12)} mo`
                        : `${scenarios.high.solvedYears.toFixed(1)} yr`
                      : "—"}
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-center">
                  <p className="text-xs font-medium text-muted-foreground">
                    Low (4%)
                  </p>
                  <p className="text-lg font-semibold text-foreground">
                    {fmt(scenarios.low.futureValue)}
                  </p>
                </div>
                <div className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-center">
                  <p className="text-xs font-medium text-muted-foreground">
                    Medium (7%)
                  </p>
                  <p className="text-lg font-semibold text-foreground">
                    {fmt(scenarios.medium.futureValue)}
                  </p>
                </div>
                <div className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-center">
                  <p className="text-xs font-medium text-muted-foreground">
                    High (10%)
                  </p>
                  <p className="text-lg font-semibold text-foreground">
                    {fmt(scenarios.high.futureValue)}
                  </p>
                </div>
              </>
            )}
          </div>

          {mounted && pieData.length >= 2 && (
            <div className="rounded-lg border border-border bg-card p-5">
              <p className="mb-4 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Breakdown
              </p>
              <PieChartWrapper data={pieData} height={200} />
            </div>
          )}

          <div className="rounded-lg border border-border bg-muted/30">
            <button
              type="button"
              onClick={() => setShowYearlyTable((v) => !v)}
              className="flex w-full cursor-pointer items-center gap-2 px-4 py-3 text-left text-sm font-medium text-foreground hover:bg-muted/50"
            >
              <Table2 className="size-4 text-muted-foreground" />
              Year-by-year breakdown
              {showYearlyTable ? (
                <ChevronDown className="size-4 shrink-0" />
              ) : (
                <ChevronRight className="size-4 shrink-0" />
              )}
            </button>
            {showYearlyTable && (
              <div className="border-t border-border">
                <div className="max-h-[300px] overflow-auto px-2 pb-2 pt-2">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-left">
                        <th className="px-3 py-1.5 font-medium text-foreground">
                          Year
                        </th>
                        <th className="px-3 py-1.5 text-right font-medium text-foreground">
                          Balance
                        </th>
                        <th className="px-3 py-1.5 text-right font-medium text-foreground">
                          Contribution
                        </th>
                        <th className="px-3 py-1.5 text-right font-medium text-foreground">
                          Interest
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.yearlyBreakdown
                        .slice(0, yearlyTableRowsShown)
                        .map((row) => (
                          <tr
                            key={row.year}
                            className="border-b border-border/50"
                          >
                            <td className="px-3 py-1.5 text-muted-foreground">
                              {row.year}
                            </td>
                            <td className="px-3 py-1.5 text-right text-muted-foreground">
                              {fmt(row.balance)}
                            </td>
                            <td className="px-3 py-1.5 text-right text-muted-foreground">
                              {fmt(row.contribution)}
                            </td>
                            <td className="px-3 py-1.5 text-right text-muted-foreground">
                              {fmt(row.interest)}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
                {result.yearlyBreakdown.length > yearlyTableRowsShown && (
                  <div className="border-t border-border px-3 py-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-xs text-muted-foreground"
                      onClick={() =>
                        setYearlyTableRowsShown((n) =>
                          Math.min(
                            n + 12,
                            result.yearlyBreakdown.length
                          )
                        )
                      }
                    >
                      Load 12 more years
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="ml-2 text-xs text-muted-foreground"
                      onClick={() =>
                        setYearlyTableRowsShown(
                          result.yearlyBreakdown.length
                        )
                      }
                    >
                      Show all ({result.yearlyBreakdown.length})
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      }
      chartSection={
        mounted ? (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Low (4%), Medium (7%), and High (10%) return scenarios.
            </p>
            <LineChartWrapper
              data={scenarioChartData}
              currency={formValues.currency}
              dataKeys={[
                { key: "low", color: SCENARIO_COLORS.low, name: "Low (4%)" },
                {
                  key: "medium",
                  color: SCENARIO_COLORS.medium,
                  name: "Medium (7%)",
                },
                {
                  key: "high",
                  color: SCENARIO_COLORS.high,
                  name: "High (10%)",
                },
              ]}
            />
          </div>
        ) : (
          <div className="h-[300px] w-full animate-pulse rounded-lg bg-muted" />
        )
      }
      explanationSection={
        <div className="space-y-6">
          <div>
            <h2 className="mb-3 text-lg font-semibold text-foreground">
              How investment growth works
            </h2>
            <p className="mb-3 text-muted-foreground">
              Your money grows through compound returns: you earn returns on your
              initial investment and on reinvested gains. Adding regular
              contributions accelerates growth because each contribution earns
              returns from the day it&apos;s made. The stock market has
              historically returned about 7% per year on average; bonds about 4%.
              This calculator projects growth assuming a fixed rate—real
              investments fluctuate.
            </p>
          </div>

          <div>
            <h3 className="mb-2 text-base font-semibold text-foreground">
              Formula
            </h3>
            <p className="mb-3 font-mono text-sm text-foreground">
              FV = P(1 + r/n)^(nt) + PMT × [((1 + r/n)^(nt) − 1) / (r/n)]
            </p>
            <p className="mb-3 text-muted-foreground">
              With regular contributions, each contribution compounds from when
              it&apos;s made. Inflation adjustment shows your future balance in
              today&apos;s purchasing power.
            </p>
          </div>

          <div>
            <h2 className="mb-3 text-lg font-semibold text-foreground">
              Example
            </h2>
            <p className="mb-3 text-muted-foreground">
              $10,000 initial + $500/month at 7% for 20 years:
            </p>
            <ExplanationTable
              headers={["Variable", "Value"]}
              rows={[
                ["Initial investment", "$10,000"],
                ["Monthly contribution", "$500"],
                ["Annual return", "7%"],
                ["Years", "20"],
                ["Future value", "~$311,000"],
                ["Total contributions", "$120,000"],
                ["Interest earned", "~$181,000"],
              ]}
            />
          </div>

          <div>
            <h2 className="mb-3 text-lg font-semibold text-foreground">
              Limitations
            </h2>
            <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
              <li>Assumes fixed rate; real returns vary year to year.</li>
              <li>Does not account for taxes, fees, or market downturns.</li>
              <li>Use for planning; past performance doesn&apos;t guarantee future results.</li>
            </ul>
          </div>
        </div>
      }
    />
  );
}
